import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Folder,
  FileText,
  ChevronRight,
  Home,
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { apiClient, GitHubFile, GitHubRepository } from '../../api/client';

interface GitHubFileBrowserProps {
  repository: GitHubRepository;
  onFileSelect: (file: GitHubFile, fullPath: string) => void;
  onNewFileSelect?: (directoryPath: string, fileName: string) => void;
  selectedPath?: string;
  className?: string;
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

export const GitHubFileBrowser: React.FC<GitHubFileBrowserProps> = ({
  repository,
  onFileSelect,
  onNewFileSelect,
  selectedPath,
  className = '',
}) => {
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState<GitHubFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingNewFile, setIsCreatingNewFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');

  // Parse current path into breadcrumbs
  const breadcrumbs: BreadcrumbItem[] = [
    { name: repository.repo_name, path: '' }
  ];

  if (currentPath) {
    const pathParts = currentPath.split('/').filter(part => part);
    pathParts.forEach((part, index) => {
      const path = pathParts.slice(0, index + 1).join('/');
      breadcrumbs.push({ name: part, path });
    });
  }

  // Load files for current path
  const loadFiles = async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getGitHubRepositoryFiles(repository.id, path || undefined);
      setFiles(response.files);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load repository files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Load files when path changes
  useEffect(() => {
    loadFiles(currentPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, repository.id]);

  // Navigate to a specific path
  const navigateToPath = (path: string) => {
    setCurrentPath(path);
    setSearchTerm('');
  };

  // Handle folder click
  const handleFolderClick = (folder: GitHubFile) => {
    navigateToPath(folder.path);
  };

  // Handle file click
  const handleFileClick = (file: GitHubFile) => {
    onFileSelect(file, file.path);
  };

  // Handle new file creation
  const handleCreateNewFile = () => {
    if (!newFileName.trim()) return;

    const fullPath = currentPath ? `${currentPath}/${newFileName}` : newFileName;
    if (onNewFileSelect) {
      onNewFileSelect(currentPath, fullPath);
    }

    // Reset state
    setIsCreatingNewFile(false);
    setNewFileName('');
  };

  // Handle new file cancel
  const handleCancelNewFile = () => {
    setIsCreatingNewFile(false);
    setNewFileName('');
  };

  // Filter files based on search term
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group files by type (directories first, then files)
  const groupedFiles = [...filteredFiles].sort((a, b) => {
    if (a.type === 'dir' && b.type === 'file') return -1;
    if (a.type === 'file' && b.type === 'dir') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Folder className="h-5 w-5" />
          Browse Repository Files
        </CardTitle>

        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => navigateToPath(crumb.path)}
              >
                {index === 0 ? (
                  <div className="flex items-center gap-1">
                    <Home className="h-3 w-3" />
                    {crumb.name}
                  </div>
                ) : (
                  crumb.name
                )}
              </Button>
              {index < breadcrumbs.length - 1 && (
                <ChevronRight className="h-3 w-3" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Search and Create New File */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          {onNewFileSelect && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCreatingNewFile(true)}
              className="h-9 px-3"
            >
              <FileText className="h-4 w-4 mr-1" />
              New File
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Error State */}
        {error && (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Loading files...</span>
          </div>
        )}

        {/* New File Creation Input */}
        {isCreatingNewFile && (
          <div className="p-4 border-b bg-muted/50">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-foreground">
                  Create New File in: {currentPath || '/'}
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Enter filename (e.g., policies.yaml)"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateNewFile();
                    } else if (e.key === 'Escape') {
                      handleCancelNewFile();
                    }
                  }}
                  className="flex-1"
                  autoFocus
                />
                <div className="flex gap-2 sm:gap-2 sm:flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={handleCreateNewFile}
                    disabled={!newFileName.trim()}
                    className="flex-1 sm:flex-none"
                  >
                    Create
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelNewFile}
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File List */}
        {!loading && !error && (
          <div className="max-h-96 overflow-y-auto">
            {groupedFiles.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {searchTerm ? 'No files match your search' : 'This directory is empty'}
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {groupedFiles.map((file) => (
                  <div
                    key={file.path}
                    className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors hover:bg-accent ${
                      selectedPath === file.path ? 'bg-accent border-l-2 border-primary' : ''
                    }`}
                    onClick={() => file.type === 'dir' ? handleFolderClick(file) : handleFileClick(file)}
                  >
                    <div className="flex-shrink-0">
                      {file.type === 'dir' ? (
                        <Folder className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {file.name}
                      </div>
                      {file.type === 'file' && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                          {file.name.endsWith('.yaml') || file.name.endsWith('.yml') ? (
                            <Badge variant="secondary" className="text-xs whitespace-nowrap">
                              YAML
                            </Badge>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {file.type === 'dir' && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};